import {
  LitElement,
  css,
  html,
  classMap,
  styleMap,
  ref,
  createRef,
  ifDefined,
  enableBodyScroll,
  disableBodyScroll,
} from '@hh/global.js'

class HHDetailsModal extends LitElement {
  static properties = {
    breakpoint: {
      type: String,
    },
    label: {
      type: String,
    },
    selector: {
      type: String,
    },
    open: {
      type: Boolean,
      attribute: 'is-open',
      reflect: true,
    },
    forcedOpen: {
      attribute: 'open',
    },
    autoFocusedAttribute: {
      attribute: 'auto-focused-attribute',
    },
    lockHeight: {
      type: Boolean,
      attribute: 'lock-height',
    },
    heightOffset: {
      type: Number,
      attribute: 'height-offset',
    },
    scrollLock: {
      type: Boolean,
      attribute: 'scroll-lock',
    },
    disableTrigger: {
      type: Boolean,
      attribute: 'disable-trigger',
    },
    haspopup: {
      type: String,
    },
    direction: {
      type: String,
    },
    _height: {
      state: true,
    },
  }

  $container = createRef()
  $details = createRef()

  get _slottedScrollingContainer() {
    const slot = this.shadowRoot.querySelector('slot[name="details"]')
    return slot.assignedElements()[0].firstElementChild
  }

  constructor() {
    super()

    this.open = false
    this.lockHeight = false
    this.heightOffset = 3
    this.breakpoint = '(min-width: 990px)'
    this.bodyListener = this.bodyListener.bind(this)
  }

  requestUpdate(name, value) {
    // Allows parents to close open modals
    if (name === 'forcedOpen') {
      this.toggle(false, !this.forcedOpen)
    }
    return super.requestUpdate(name, value)
  }

  interceptEvent(e) {
    e._intercepted = true
  }

  toggle(e = false, value = undefined, focus = false) {
    if (e.type === 'keyup' && e.code.toUpperCase() !== 'ENTER') {
      return true
    }

    e && e.preventDefault()

    this.open = typeof value === 'undefined' ? !this.open : value
    this[`${this.open ? 'add' : 'remove'}BodyListener`]()
    this.open && this.lockHeight && this.setHeight()

    this.broadcast(this.open ? 'modalOpened' : 'modalClosed')

    if (this.disableTrigger) {
      const element = this.querySelector('.js-modal-trigger')
      element?.setAttribute('tabindex', `${(this.open ? '-1' : '0')}`)
    }

    if (focus) {
      this.querySelector('[slot="summary"]').contentEditable = true;
      this.querySelector('[slot="summary"]')?.focus()
      this.querySelector('[slot="summary"]').contentEditable = false;
    }

    document.querySelectorAll('hh-details-modal').forEach(element => {
      if (element != this) element.open = false
    })
  }

  closeFromDetails(e) {
    const path = e.composedPath()
    if (
      path.some((element) => {
        return element.matches && element.matches('[data-close]')
      })
    ) {
      this.toggle()

      const opener =
        this.querySelector('.js-modal-trigger') ||
        this.closest('hh-details-modal')?.querySelector('.js-modal-trigger')

      requestAnimationFrame(() => {
        opener?.focus({ preventScroll: true })
      })
    }
  }

  onTriggerKeyDown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      e.stopPropagation()
      this.toggle()
    }
  }  

  closeWithEscape(e) {
    if (e.code.toUpperCase() === 'ESCAPE') {
      this.toggle()
    }
  }

  isMobile() {
    return !window.matchMedia(this.breakpoint).matches
  }

  addBodyListener() {
    document.querySelector('html').classList.add('overflow-hidden')
    document.body.addEventListener('click', this.bodyListener)
  }

  removeBodyListener() {
    document.querySelector('html').classList.remove('overflow-hidden')
    document.body.removeEventListener('click', this.bodyListener)
  }

  bodyListener(e) {
    !e._intercepted && this.toggle()
  }

  setHeight() {
    const header = document.querySelector('.js-header-wrapper')
    const headerHeight = header?.getBoundingClientRect().height || 0

    this._height = this.isMobile()
      ? `calc(var(--hh-window-height, 100dvh) - ${headerHeight}px)`
      : ''
  }

  broadcast(eventName) {
    this.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
        composed: true,
        detail: {
          value: this,
        },
      })
    )
  }

  render() {
    return html` <!-- @ts-ignore no-incompatible-type-binding -->
      <details
        ?open="${this.open}"
        @click="${this.interceptEvent}"
        ${ref(this.$container)}
      >
        <summary
          @keyup="${this.toggle}"
          @keydown="${this.onTriggerKeyDown}"
          aria-expanded="${this.open}"
          aria-haspopup="${ifDefined(this.haspopup ? `${this.haspopup}` : 'dialog')}"
          role="button"
          tabindex="-1"
          ${ref(this.$container)}
        >
          <slot
            @click="${this.toggle}"
            class="${classMap({ 'is-open': this.open })}"
            name="summary"            
          ></slot>
        </summary>
        <div
          class="content"
          role="dialog"
          aria-modal="true"
          aria-label="${this.label}"
          style="${styleMap({ height: this._height })}"
          ${ref(this.$details)}
        >
          <ls-focus-trap
            active="${this.open}"
            direction="${this.direction}"
            auto-focused-attribute="${this.autoFocusedAttribute}"
          >
            <slot
              name="details"
              @keyup="${this.closeWithEscape}"
              @click="${this.closeFromDetails}"
            ></slot>
          </ls-focus-trap>
        </div>
      </details>`
  }

  static styles = css`
    summary {
      list-style-type: none;
    }

    summary:focus-visible {
      outline: 0.1250rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }

    summary::-webkit-details-marker {
      display: none;
    }

    .content {
      position: var(--hh-position, absolute);
      top: var(--hh-top);
      left: 0;
      right: 0;
      bottom: 0;
      background: var(--hh-modal-background);
      height: var(--hh-modal-height);
      z-index: var(--hh-z-index-inner, 1);
    }

    ::slotted([slot='details']) {
      width: 100%;
      display: flex;
    }

    :host {
      z-index: var(--hh-z-index, 100);
    }

    :host([is-open]),
    :host([open]):not([open="false"]) {
      z-index: var(--hh-z-index-opened, var(--hh-z-index, 100));
    }
  `
}

customElements.define('hh-details-modal', HHDetailsModal)
